/**
 * Presets de motion reutilizables (variants, springs, viewport).
 * Solo animan transform/opacity para no provocar layout shift.
 * El respeto a prefers-reduced-motion lo aplica <MotionConfig reducedMotion="user">
 * en App.js: con esa preferencia se omiten las animaciones de transform y quedan
 * solo los fundidos de opacidad.
 */
import { useReducedMotion } from 'motion/react';

export { useReducedMotion };

// Equivalentes JS de los tokens de Global.css (--ease-*, --duration-*)
export const EASE = {
  out: [0.22, 1, 0.36, 1],
  inOut: [0.65, 0, 0.35, 1],
  spring: [0.34, 1.56, 0.64, 1],
};

export const DURATION = {
  instant: 0.09,
  fast: 0.15,
  normal: 0.28,
  slow: 0.45,
  slower: 0.7,
};

export const SPRING = {
  // Controles táctiles: respuesta inmediata, casi sin rebote
  snappy: { type: 'spring', stiffness: 520, damping: 34, mass: 0.8 },
  // Paneles / modales: asentamiento suave
  soft: { type: 'spring', stiffness: 320, damping: 30, mass: 0.9 },
  // Indicadores (tabs, pills) con ligero rebote
  bouncy: { type: 'spring', stiffness: 420, damping: 26, mass: 0.8 },
};

export const DISTANCE = 16;

export const fadeUp = {
  hidden: { opacity: 0, y: DISTANCE },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.slow, ease: EASE.out },
  },
  exit: { opacity: 0, y: -8, transition: { duration: DURATION.fast, ease: EASE.inOut } },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.normal, ease: EASE.out } },
  exit: { opacity: 0, transition: { duration: DURATION.fast, ease: EASE.inOut } },
};

// Fade-through (Material): sale rápido, entra con un leve escalado
export const fadeThrough = {
  hidden: { opacity: 0, scale: 0.985 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: DURATION.normal, ease: EASE.out, delay: 0.04 },
  },
  exit: {
    opacity: 0,
    transition: { duration: DURATION.fast, ease: EASE.inOut },
  },
};

// Shared-axis X sutil para transiciones de ruta
export const sharedAxisX = {
  hidden: { opacity: 0, x: 12 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: DURATION.normal, ease: EASE.out },
  },
  exit: {
    opacity: 0,
    x: -12,
    transition: { duration: DURATION.fast, ease: EASE.inOut },
  },
};

export const scaleIn = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: { opacity: 1, scale: 1, transition: SPRING.soft },
  exit: { opacity: 0, scale: 0.96, transition: { duration: DURATION.fast, ease: EASE.inOut } },
};

export const slideInLeft = {
  hidden: { x: '-100%' },
  visible: { x: 0, transition: SPRING.soft },
  exit: { x: '-100%', transition: { duration: DURATION.normal, ease: EASE.inOut } },
};

export const staggerContainer = (stagger = 0.07, delayChildren = 0) => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren } },
});

export const staggerItem = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.slow, ease: EASE.out },
  },
};

// whileInView: una sola vez. amount "some" evita que bloques más altos que el
// viewport nunca alcancen un umbral porcentual.
export const VIEWPORT_ONCE = { once: true, amount: 'some', margin: '0px 0px -8% 0px' };

// Mezcla un retardo en la transición de la variante "visible"
export const withDelay = (variants, delay = 0) =>
  delay
    ? {
        ...variants,
        visible: {
          ...variants.visible,
          transition: { ...variants.visible.transition, delay },
        },
      }
    : variants;

// Feedback táctil para elementos interactivos
export const tapScale = { scale: 0.97 };
export const hoverLift = { y: -2 };
